// UPPDRAG-38 — async loader for league pricing. Pure logic lives in
// ./leaguePricingCore.ts so vitest can import the calculator without
// booting the supabase client.

import { supabase } from "@/integrations/supabase/client";
import {
  calculateLeaguePriceForPlayer,
  type LeagueMembershipPricingRule,
  type LeaguePriceResult,
} from "./leaguePricingCore";

export type {
  LeagueMembershipPricingRule,
  LeaguePriceResult,
  PricingInputs,
} from "./leaguePricingCore";
export { calculateLeaguePriceForPlayer } from "./leaguePricingCore";

interface LoadInputs {
  leagueId: string;
  /** Player's user_id. If null → returns full-price result. */
  userId: string | null;
  /** League's organization_id. */
  organizationId: string;
  /** Base price in kronor (from leagues.cost_per_player). */
  basePriceKr: number;
}

/**
 * Load tier rules + player's active memberships + plan names from supabase,
 * then delegate to the pure calculator.
 */
export async function loadLeaguePricingForPlayer(
  input: LoadInputs,
): Promise<LeaguePriceResult> {
  const { leagueId, userId, organizationId, basePriceKr } = input;

  // 1. Pull all pricing rules for this league.
  const { data: rulesData, error: rulesErr } = await supabase
    .from("league_membership_pricing" as any)
    .select("membership_plan_id, discount_percent, is_free")
    .eq("league_id", leagueId);
  if (rulesErr) throw rulesErr;
  // Cast through unknown — supabase doesn't infer row shapes for
  // `.from(... as any)` selects, and the runtime shape matches our type.
  const rules = ((rulesData ?? []) as unknown) as LeagueMembershipPricingRule[];

  // 2. Pull plan names (for display), scoped to org's active plans.
  const { data: plansData } = await supabase
    .from("membership_plans" as any)
    .select("id, name")
    .eq("organization_id", organizationId)
    .eq("is_active", true);
  const planNameById: Record<string, string> = {};
  for (const p of ((plansData ?? []) as unknown) as { id: string; name: string }[]) {
    planNameById[p.id] = p.name;
  }

  // 3. No user → full price.
  if (!userId) {
    return calculateLeaguePriceForPlayer({
      basePriceKr,
      playerActivePlanIds: [],
      rules,
      planNameById,
    });
  }

  // 4. Pull player's active memberships in this org.
  const { data: memData, error: memErr } = await supabase
    .from("memberships" as any)
    .select("plan_id, status")
    .eq("user_id", userId)
    .eq("organization_id", organizationId)
    .eq("status", "active");
  if (memErr) throw memErr;
  const playerActivePlanIds = (((memData ?? []) as unknown) as { plan_id: string }[])
    .map((m) => m.plan_id);

  return calculateLeaguePriceForPlayer({
    basePriceKr,
    playerActivePlanIds,
    rules,
    planNameById,
  });
}
